import { ALBUM_SECTIONS } from '../albumData';

// Mapeo prefix (FWC, MEX, RSA, ..., CC) → { from, count, sectionId, isFwc }.
// Construido a partir de ALBUM_SECTIONS para que cualquier cambio del
// album se refleje sin tocar este parser.
const PREFIX_MAP = (() => {
  const m = new Map();
  for (const sec of ALBUM_SECTIONS) {
    if (sec.codePrefix) {
      m.set(sec.codePrefix.toUpperCase(), {
        from: sec.from,
        count: sec.to - sec.from + 1,
        sectionId: sec.id,
        isFwc: sec.codePrefix === 'FWC',
      });
    }
    if (sec.countries) {
      sec.countries.forEach((code, i) => {
        m.set(code.toUpperCase(), {
          from: sec.from + i * 20,
          count: 20,
          sectionId: sec.id,
          isFwc: false,
        });
      });
    }
  }
  return m;
})();

const ALL_PREFIXES = [...PREFIX_MAP.keys()];

/**
 * Inserta saltos de línea antes de los markers de modo y antes de cada
 * sección — así el parser line-based funciona aunque el usuario haya
 * pegado el mensaje todo en una sola línea (ej. al copiar desde un
 * chat de WhatsApp que perdió los saltos).
 *
 * Soporta los markers en EN ("I need", "Swaps", "Download the app") y
 * en ES ("Me faltan", "Repetidas", "Descarga la app") porque figuritas.app
 * cambia el copy según el idioma del usuario.
 */
function normalizeFiguritasText(text) {
  let normalized = String(text);
  // Markers de modo (case-insensitive). Quedan rodeados por \n para que
  // la detección de modo en el loop principal los encuentre como líneas.
  normalized = normalized.replace(/\bI need\b/gi, '\n$&\n');
  normalized = normalized.replace(/\bSwaps\b/gi, '\n$&\n');
  normalized = normalized.replace(/\bDownload the app\b/gi, '\n$&\n');
  // Equivalentes en español.
  normalized = normalized.replace(/\bMe falta(?:n|s)?\b/gi, '\n$&\n');
  normalized = normalized.replace(/\bRepetida(?:s)?\b/gi, '\n$&\n');
  normalized = normalized.replace(/\bDescarga(?:r)? la app\b/gi, '\n$&\n');
  // Antes de cada sección "PREFIX <emoji o espacio>:" insertamos un \n.
  // El prefijo son 2..4 letras MAYÚSCULAS al borde de palabra. El
  // contenido entre el prefijo y los `:` puede tener emojis multi-byte.
  normalized = normalized.replace(/(\s)([A-Z]{2,4})\b([^:\n]*:)/g, '$1\n$2$3');
  return normalized;
}

/**
 * Parsea el mensaje "Missing & swaps" de figuritas.app.
 *
 * Reglas:
 *  - "I need" lista los slots que el usuario NO tiene.
 *  - "Swaps" lista los slots con repetida.
 *  - Las sub-categorías de FWC (🏆, 🌎, 📜) se unifican como FWC. El "00"
 *    es la portada → slot 20 en nuestra numeración (1-indexed).
 *  - Devuelve `mentionedPrefixes` para que el caller sepa qué prefijos
 *    aparecieron — la lógica de "qué hacer con los no mencionados" la
 *    decide el caller (applyImportToBytes).
 *
 * Acepta el input multi-línea original o pegado en una sola línea (los
 * markers se reinyectan en `normalizeFiguritasText`).
 *
 * Regex acepta 2..4 letras mayúsculas para cubrir CC (2 letras), FWC y
 * los códigos de país (3 letras).
 */
export function parseFiguritasAppMessage(text) {
  const needSet = new Set();
  const swapSet = new Set();
  const mentionedPrefixes = new Set();
  const unrecognized = [];

  if (!text || typeof text !== 'string') {
    return { needSet, swapSet, mentionedPrefixes, unrecognized };
  }

  const lines = normalizeFiguritasText(text).split(/\r?\n/);
  let mode = null; // 'need' | 'swap' | null

  // "FWC 🏆: 00, 2, 3, 4"  o  "MEX 🇲🇽: 1, 3..."  o  "CC 🥤: 1, 2..."
  const lineRegex = /^([A-Z]{2,4})\b[^:]*:\s*(.+)$/;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    const lower = line.toLowerCase();
    // Markers en EN y ES. "Me faltan/Me falta/Me faltas" y "Repetidas/Repetida"
    // son los equivalentes en español de "I need" y "Swaps". "Descarga"/"Download"
    // marca el final del mensaje (el link de la app).
    if (lower.startsWith('i need') || lower.startsWith('me falta')) { mode = 'need'; continue; }
    if (lower.startsWith('swap') || lower.startsWith('repetida')) { mode = 'swap'; continue; }
    if (lower.startsWith('download') || lower.startsWith('descarga') || /^https?:/i.test(line)) { mode = null; continue; }
    if (mode == null) continue;

    const m = lineRegex.exec(line);
    if (!m) {
      unrecognized.push(line);
      continue;
    }
    const prefix = m[1].toUpperCase();
    const info = PREFIX_MAP.get(prefix);
    if (!info) {
      unrecognized.push(line);
      continue;
    }

    mentionedPrefixes.add(prefix);

    const numTokens = m[2].split(/[,\s;]+/).map(s => s.trim()).filter(Boolean);
    for (const tok of numTokens) {
      const num = parseInt(tok, 10);
      if (!Number.isFinite(num)) continue;
      let slot;
      if (info.isFwc && num === 0) {
        // FWC "00" → portada → slot 20.
        slot = info.from + info.count - 1;
      } else if (info.isFwc && num >= 1 && num < info.count) {
        slot = info.from + num - 1;
      } else if (!info.isFwc && num >= 1 && num <= info.count) {
        slot = info.from + num - 1;
      } else {
        continue;
      }
      if (mode === 'need') needSet.add(slot);
      else if (mode === 'swap') swapSet.add(slot);
    }
  }

  return { needSet, swapSet, mentionedPrefixes, unrecognized };
}

/**
 * Aplica un parseo sobre los bytes actuales del album.
 *
 *  - Prefijo MENCIONADO: para cada slot, 0 si está en need, 2 si está en
 *    swap, 1 si está poseído sin repetida.
 *  - Prefijo NO mencionado y no es CC (FWC, países): el usuario tiene todas
 *    las figuritas de esa sección → todas pasan a 1 (sin tocar repes
 *    porque no las conocemos).
 *  - Prefijo CC NO mencionado: depende de `ccChoice`:
 *      'all'      → marcar todas las CC como poseídas (1).
 *      'none'     → marcar todas las CC como faltantes (0).
 *      'preserve' → no tocar (default).
 *
 *  conflicts = slots actualmente poseídos que pasarían a missing.
 *  No cuentan repes: lo único que cuenta es si estaban en >=1.
 */
export function applyImportToBytes(currentBytes, parsed, options = {}) {
  const ccChoice = options.ccChoice || 'preserve';
  const next = new Uint8Array(currentBytes);
  const conflicts = [];

  for (const prefix of ALL_PREFIXES) {
    const info = PREFIX_MAP.get(prefix);
    const slotFrom = info.from;
    const slotEnd = info.from + info.count;
    const isMentioned = parsed.mentionedPrefixes.has(prefix);

    if (isMentioned) {
      for (let n = slotFrom; n < slotEnd; n++) {
        const idx = n - 1;
        const wasOwned = currentBytes[idx] >= 1;
        if (parsed.needSet.has(n)) {
          if (wasOwned) conflicts.push(n);
          next[idx] = 0;
        } else {
          next[idx] = parsed.swapSet.has(n) ? 2 : 1;
        }
      }
    } else if (prefix === 'CC') {
      if (ccChoice === 'all') {
        for (let n = slotFrom; n < slotEnd; n++) next[n - 1] = 1;
      } else if (ccChoice === 'none') {
        for (let n = slotFrom; n < slotEnd; n++) {
          const idx = n - 1;
          if (currentBytes[idx] >= 1) conflicts.push(n);
          next[idx] = 0;
        }
      }
      // 'preserve' → dejar como está
    } else {
      // FWC o país no mencionado → todas poseídas (figuritas.app omite
      // las secciones que están 100% completas).
      for (let n = slotFrom; n < slotEnd; n++) {
        next[n - 1] = 1;
      }
    }
  }

  conflicts.sort((a, b) => a - b);
  return { nextBytes: next, conflicts };
}

/**
 * Resumen para el UI: cuántas figuritas tendrías al confirmar y cuántos
 * países quedaron implícitamente como "todas poseídas" por no aparecer
 * en el mensaje.
 *
 * No incluye CC en `ownedCount` cuando ccChoice es 'preserve' porque no
 * sabemos qué va a pasar — el caller debe llamar de nuevo con el choice
 * elegido si quiere ver el total final.
 */
export function summarizeImport(parsed, options = {}) {
  const ccChoice = options.ccChoice || 'preserve';
  let ownedCount = 0;
  const inferredFullPrefixes = [];

  for (const prefix of ALL_PREFIXES) {
    const info = PREFIX_MAP.get(prefix);
    const slotFrom = info.from;
    const slotEnd = info.from + info.count;
    const isMentioned = parsed.mentionedPrefixes.has(prefix);

    if (isMentioned) {
      for (let n = slotFrom; n < slotEnd; n++) {
        if (!parsed.needSet.has(n)) ownedCount++;
      }
    } else if (prefix === 'CC') {
      if (ccChoice === 'all') ownedCount += info.count;
      // 'none' / 'preserve' → 0 contribución
    } else {
      ownedCount += info.count;
      inferredFullPrefixes.push(prefix);
    }
  }

  return {
    ownedCount,
    swapCount: parsed.swapSet.size,
    ccMentioned: parsed.mentionedPrefixes.has('CC'),
    inferredFullPrefixes, // FWC/países no mencionados → "tenés todas"
  };
}

export { ALL_PREFIXES, PREFIX_MAP };
